import {React, styled} from 'common'
import {Colors} from 'appearance'
import {useState} from 'react'
import Page from 'components/Page/Page'
import GeneralSettings from './Settings/GeneralSettings'
import InfoSettings from './Settings/InfoSettings'
import QueueSettings from './Settings/QueueSettings'
import ProfileSettings from './Settings/ProfileSettings'
import {
    Box,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Tab,
    Tabs,
    Typography,
} from '@mui/material'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import StorageRoundedIcon from '@mui/icons-material/StorageRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import {useTranslation} from 'react-i18next'
import useResponsive from 'logic/useResponsive'
import {useGetCurrentUserQuery} from 'store/Store'
import {hasPermission} from 'services/utils/permissionHelpers'

const ALL_TABS = [
    {
        key: 'general',
        icon: <TuneRoundedIcon fontSize="small" />,
        translationKey: 'settings.general',
        resource: 'CONFIGS',
    },
    {key: 'profile', icon: <PersonRoundedIcon fontSize="small" />, translationKey: 'profile.title'},
    {
        key: 'info',
        icon: <InfoRoundedIcon fontSize="small" />,
        translationKey: 'settings.info',
        resource: 'CONFIGS',
    },
    {
        key: 'queues',
        icon: <StorageRoundedIcon fontSize="small" />,
        translationKey: 'settings.queues',
        resource: 'QUEUE',
    },
]

const Settings = () => {
    const {t} = useTranslation()
    const {data: user} = useGetCurrentUserQuery()
    const {smallScreen} = useResponsive()

    const tabs = ALL_TABS.filter(
        (tab) => !tab.resource || hasPermission(user, tab.resource, 'READ')
    )

    const [activeTab, setActiveTab] = useState(tabs[0]?.key ?? 'profile')
    const activeTabDef = tabs.find((tab) => tab.key === activeTab)
    const activeTabIndex = tabs.findIndex((tab) => tab.key === activeTab)

    const renderContent = () => {
        switch (activeTab) {
            case 'general':
                return <GeneralSettings />
            case 'profile':
                return <ProfileSettings />
            case 'info':
                return <InfoSettings />
            case 'queues':
                return <QueueSettings />
            default:
                return null
        }
    }

    const content = (
        <ContentCard>
            {/* Mobile: tab bar header */}
            {smallScreen ? (
                <Tabs
                    value={activeTabIndex}
                    onChange={(_, i) => setActiveTab(tabs[i].key)}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    textColor="primary">
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.key}
                            icon={tab.icon}
                            label={t(tab.translationKey)}
                            iconPosition="start"
                            sx={{fontSize: 13, minHeight: 48}}
                        />
                    ))}
                </Tabs>
            ) : (
                <ContentHeader>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{color: 'primary.main', display: 'flex', alignItems: 'center'}}>
                            {activeTabDef?.icon}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {t(activeTabDef?.translationKey)}
                        </Typography>
                    </Box>
                </ContentHeader>
            )}
            <Divider />
            <ContentBody>{renderContent()}</ContentBody>
        </ContentCard>
    )

    return (
        <Page>
            {smallScreen ? (
                <MobileLayout>{content}</MobileLayout>
            ) : (
                <SettingsLayout>
                    <Sidebar>
                        <SidebarLabel>{t('header.profile.settings')}</SidebarLabel>
                        <List disablePadding>
                            {tabs.map((tab) => {
                                const isActive = activeTab === tab.key
                                return (
                                    <ListItemButton
                                        key={tab.key}
                                        selected={isActive}
                                        onClick={() => setActiveTab(tab.key)}
                                        sx={{
                                            borderRadius: '8px',
                                            mb: 0.5,
                                            px: 1.5,
                                            py: 1,
                                            '&.Mui-selected': {
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                '&:hover': {bgcolor: 'primary.dark'},
                                                '& .MuiListItemIcon-root': {color: 'white'},
                                            },
                                        }}>
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 32,
                                                color: isActive ? 'white' : 'text.secondary',
                                            }}>
                                            {tab.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={t(tab.translationKey)}
                                            primaryTypographyProps={{
                                                fontSize: 14,
                                                fontWeight: isActive ? 600 : 400,
                                            }}
                                        />
                                    </ListItemButton>
                                )
                            })}
                        </List>
                    </Sidebar>
                    <ContentArea>{content}</ContentArea>
                </SettingsLayout>
            )}
        </Page>
    )
}

export default Settings

const MobileLayout = styled.div`
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding-bottom: 16px;
`

const SettingsLayout = styled.div`
    display: flex;
    gap: 20px;
    width: 100%;
    height: 100%;
    overflow: hidden;
`

const Sidebar = styled.div`
    width: 190px;
    flex-shrink: 0;
    padding-top: 4px;
`

const SidebarLabel = styled.p`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: ${Colors.APP_FADED_GREY};
    margin: 0 0 10px 6px;
`

const ContentArea = styled.div`
    flex: 1;
    overflow-y: auto;
    min-width: 0;
    padding-bottom: 16px;
`

const ContentCard = styled(Paper)`
    border-radius: 12px !important;
    border: 1px solid ${Colors.LIGHT_GRAY} !important;
    overflow: hidden;
    box-shadow: none !important;
`

const ContentHeader = styled.div`
    padding: 16px 20px;
`

const ContentBody = styled.div`
    padding: 8px 20px 20px;
`
